/**
 * Token storage.
 *
 * The access token lives in memory only — it is never written to disk, so an
 * XSS payload cannot lift it out of storage after the fact and a stolen device
 * profile contains nothing usable. The refresh token has to survive a reload to
 * keep people signed in, so it goes to localStorage; it is single-use and
 * rotates on every refresh, which limits the value of a stale copy.
 */

const REFRESH_KEY = "ro.refreshToken";
const SCHEME_KEY = "ro.colorScheme";
const BUSINESS_KEY = "ro.activeBusinessId";

let accessToken: string | null = null;
/** Epoch ms. We refresh a little early rather than waiting for a 401. */
let accessExpiresAt = 0;

const EARLY_REFRESH_MS = 30_000;

type Listener = () => void;
const sessionEndedListeners = new Set<Listener>();

export const tokens = {
  getAccess() {
    return accessToken;
  },

  /** True when we hold a token that is not about to expire. */
  hasFreshAccess() {
    return Boolean(accessToken) && Date.now() < accessExpiresAt - EARLY_REFRESH_MS;
  },

  getRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },

  set(pair: { accessToken: string; refreshToken: string; expiresIn?: number }) {
    accessToken = pair.accessToken;
    accessExpiresAt = Date.now() + (pair.expiresIn ?? 900) * 1000;
    try {
      localStorage.setItem(REFRESH_KEY, pair.refreshToken);
    } catch {
      /* private mode — session simply won't survive a reload */
    }
  },

  clear() {
    accessToken = null;
    accessExpiresAt = 0;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  },

  /** Called when the server tells us the session is dead. */
  onSessionEnded(listener: Listener) {
    sessionEndedListeners.add(listener);
    return () => sessionEndedListeners.delete(listener);
  },

  endSession() {
    tokens.clear();
    for (const l of sessionEndedListeners) l();
  },
};

export const storageKeys = {
  refresh: REFRESH_KEY,
  colorScheme: SCHEME_KEY,
  activeBusiness: BUSINESS_KEY,
} as const;

export function readActiveBusinessId(): string | null {
  try {
    return localStorage.getItem(BUSINESS_KEY);
  } catch {
    return null;
  }
}

export function writeActiveBusinessId(id: string | null) {
  try {
    if (id) localStorage.setItem(BUSINESS_KEY, id);
    else localStorage.removeItem(BUSINESS_KEY);
  } catch {
    /* ignore */
  }
}
